import React, { useState, useCallback, useEffect } from 'react';
import Header from './components/Header';
import Stepper from '../../../components/Stepper';
import Button from '../../../components/Button';
import StatementDetailsStep from './components/StatementDetailsStep';
import VerifyDataStep from './components/VerifyDataStep';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modules, AuthRoutes } from '../../../enums';
import { Validation } from '../../../interfaces/common';
import { validateMonthlyStockForm } from './validations';
import useFetch from '../../../hooks/useFetch';
import MonthlyStockService from '../../../services/monthlyStock.service';
import CommonService from '../../../services/common.service';
import TemplateService from '../../../services/template.service';
import { GENERIC_LOOKUP_KEYS, APP_ERROR_MESSAGES, VALIDATION_FIELD_MAPPING } from '../../../utils/appConstant';
import { getCurrentMonthCode } from '../../../utils/methods';

const ALLOWED_FILE_EXTENSIONS = ['.xlsx', '.xls', '.txt'];

const AddEditView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const steps = [
    t('monthlyStock.addEdit.pageTexts.stepper.step1'),
    t('monthlyStock.addEdit.pageTexts.stepper.step2'),
  ];
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [monthlyStockId, setMonthlyStockId] = useState<number | null>(null);

  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl) {
      setMonthlyStockId(Number(idFromUrl));
      setActiveStep(1);
    }
  }, []);

  const [formData, setFormData] = useState<any>({
    stockist: '',
    branch: '',
    month: getCurrentMonthCode(),
    year: String(new Date().getFullYear()),
    template: '',
    file: null,
  });

  const [validation, setValidation] = useState<Validation>({ validate: true, fields: [] });

  const [dropdownSearchData, setDropdownSearchData] = useState({
    month: '',
    year: '',
    stockist: '',
    branch: '',
    template: '',
  });

  const handleDropdownSearchChange = useCallback((e: any, name: string) => {
    setDropdownSearchData((prev) => ({ ...prev, [name]: e.target.value }));
  }, []);

  // Month lookup (from genericLookup, sorted by id for correct month order)
  const { data: monthOptionsRaw } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: '', sortBy: 'id' },
    dependency: [],
  });

  // Template lookup
  const { data: allTemplateOptions } = useFetch({
    service: TemplateService,
    func: 'lookup',
    payload: { searchText: '' },
    dependency: [],
  });
  const { data: templateOptions } = useFetch({
    service: TemplateService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.template },
    dependency: [dropdownSearchData.template],
  });

  // Stockist lookup
  const { data: allStockistOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupStockist',
    payload: { searchText: '' },
    dependency: [],
  });
  const { data: stockistOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupStockist',
    payload: { searchText: dropdownSearchData.stockist },
    dependency: [dropdownSearchData.stockist],
  });

  // Branch lookup (depends on selected stockist)
  const { data: allBranchOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupBranch',
    payload: { searchText: '', stockistId: formData.stockist },
    dependency: [formData.stockist],
  });
  const { data: branchOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupBranch',
    payload: { searchText: dropdownSearchData.branch, stockistId: formData.stockist },
    dependency: [dropdownSearchData.branch, formData.stockist],
  });

  const handleDropdownChange = useCallback((_: any, name: string) => {
    const value = _.target.value;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
      ...(name === 'stockist' ? { branch: '' } : {}),
    }));
  }, []);

  const handleInputBlur = useCallback((event: any, name: string) => {
    const value = typeof event.target.value === 'string' ? event.target.value.trim() : event.target.value;
    const newValidation = validateMonthlyStockForm({ ...formData, [name]: value }, name);
    setValidation(newValidation);
  }, [formData]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];
    const name = file.name.toLowerCase();
    const ext = name.slice(name.lastIndexOf('.'));
    const isValid = ALLOWED_FILE_EXTENSIONS.includes(ext);

    // Reset input so the same file can be re-selected after removal
    event.target.value = '';

    if (!isValid) {
      setValidation((prev: any) => {
        const otherFields = prev.fields.filter((f: any) => f.field !== VALIDATION_FIELD_MAPPING.file);
        return {
          validate: otherFields.length === 0,
          fields: [...otherFields, { field: VALIDATION_FIELD_MAPPING.file, message: APP_ERROR_MESSAGES.monthlyStock.addEdit.invalidFileType }],
        };
      });
      return;
    }

    setFormData((prev: any) => ({ ...prev, file }));
    setValidation((prev: any) => {
      const otherFields = prev.fields.filter((f: any) => f.field !== VALIDATION_FIELD_MAPPING.file);
      return { validate: otherFields.length === 0, fields: otherFields };
    });
  }, []);

  const handleFileRemove = useCallback(() => {
    setFormData((prev: any) => ({ ...prev, file: null }));
  }, []);

  const handleNext = async () => {
    if (activeStep === 0) {
      const validationResult = validateMonthlyStockForm(formData);
      setValidation(validationResult);
      if (!validationResult.validate) return;

      try {
        setLoading(true);
        const payload = new FormData();
        payload.append('stockist_id', formData.stockist);
        payload.append('stockist_branch_id', formData.branch);
        payload.append('month', formData.month);
        payload.append('year', formData.year);
        if (formData.template) payload.append('template_id', formData.template);
        payload.append('file', formData.file);

        const response: any = await MonthlyStockService.upload(payload);
        const id = response?.data?.id;
        setMonthlyStockId(id);
        navigate(`?id=${id}`, { replace: true });
        setActiveStep(1);
      } catch {
        // Errors handled by http interceptor
      } finally {
        setLoading(false);
      }
    } else {
      if (!monthlyStockId) return;
      try {
        setLoading(true);
        await MonthlyStockService.submitStatement(monthlyStockId);
        navigate(Modules.monthlyStock + AuthRoutes.monthlyStockList);
      } catch {
        // Errors handled by http interceptor
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    navigate(Modules.monthlyStock + AuthRoutes.monthlyStockList);
  };

  return (
    <>
      <Header />

      <div className="mb-6">
        <Stepper steps={steps} activeStep={activeStep} />
      </div>

      <div className="row g-4">
        <div className="col-12">
          {activeStep === 0 && (
            <StatementDetailsStep
              formData={formData}
              validation={validation}
              onDropdownChange={handleDropdownChange}
              onInputBlur={handleInputBlur}
              onFileChange={handleFileChange}
              onFileRemove={handleFileRemove}
              monthOptions={(monthOptionsRaw || []).map((o: any) => ({ label: o.name, value: String(o.id) }))}
              templateOptions={(templateOptions || []).map((tpl: any) => ({ label: tpl.name, value: String(tpl.id) }))}
              allTemplateOptions={(allTemplateOptions || []).map((tpl: any) => ({ label: tpl.name, value: String(tpl.id) }))}
              stockistOptions={(stockistOptions || []).map((o: any) => ({ label: o.name, value: String(o.id) }))}
              allStockistOptions={(allStockistOptions || []).map((o: any) => ({ label: o.name, value: String(o.id) }))}
              branchOptions={(branchOptions || []).map((o: any) => ({ label: o.name, value: String(o.id) }))}
              allBranchOptions={(allBranchOptions || []).map((o: any) => ({ label: o.name, value: String(o.id) }))}
              dropdownSearchData={dropdownSearchData}
              onDropdownSearchChange={handleDropdownSearchChange}
            />
          )}

          {activeStep === 1 && monthlyStockId && <VerifyDataStep monthlyStockId={monthlyStockId} />}
        </div>

        {activeStep === 0 ? (
          <div className="col-12 d-flex justify-content-end mb-4">
            <Button
              className="btn btn-outline-secondary me-3 waves-effect"
              onClick={handleCancel}
              text={t('monthlyStock.addEdit.pageTexts.form.cancel')}
              disabled={loading}
            />
            <Button
              className="btn btn-primary waves-effect waves-light"
              onClick={handleNext}
              text={t('monthlyStock.addEdit.pageTexts.form.next')}
              loading={loading}
            />
          </div>
        ) : (
          <div className="col-12 d-flex justify-content-between mb-4">
            <Button
              className="btn btn-label-secondary waves-effect"
              onClick={() => setActiveStep((prev) => prev - 1)}
              text={t('monthlyStock.addEdit.pageTexts.verifyData.buttons.back')}
              disabled={loading}
            />
            <div>
              <Button
                className="btn btn-outline-secondary me-3 waves-effect"
                onClick={handleCancel}
                text={t('monthlyStock.addEdit.pageTexts.verifyData.buttons.cancel')}
                disabled={loading}
              />
              <Button
                className="btn btn-primary waves-effect waves-light"
                onClick={handleNext}
                text={t('monthlyStock.addEdit.pageTexts.verifyData.buttons.submit')}
                loading={loading}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AddEditView;
